import rawConfig from "./config.json";

export type Environment = "dev" | "prod";

export interface ChartEndpointConfig {
  chartServer: string;
  chartPath: string;
  headers?: Record<string, string>;
}

export interface SpreadV6Endpoint {
  /** Path prefix on the chart server, e.g. `"/spreadv6/L/brokt1/"`. */
  urlBase: string;
  /** Display label for this feed, e.g. `"Broker Sharp"` / `"LD4 Tier 1"`. */
  legend: string;
}

export interface VenueEndpoint {
  /** Instrument catalog + live depth snapshot, e.g. `"https://md.app.lmax.com/brokt1/instruments/depths"`. Public — no auth needed as of this writing. */
  url: string;
  headers?: Record<string, string>;
}

export interface VenueConfig {
  label: string;
  /** Lowercase venue code used in chart URLs, e.g. `"brokt1"` for `BROKT1`. */
  venue: string;
  endpoint?: VenueEndpoint;
  /**
   * Present in config but not currently used to build a fetch URL —
   * `urlBase` + `<symbol>/<interval>` 400s on the real API ("Invalid or
   * missing parameters: interval") no matter how `interval` is supplied.
   * `ConfigHelper.getChartUrl` uses `chartEndpoint.chartPath` instead,
   * which is verified working. Revisit if the real route shape turns out
   * to need something other than a trailing `/<interval>` segment.
   */
  spreadv6?: SpreadV6Endpoint[];
}

export interface EnvironmentConfig {
  host: string;
  path: string;
  /** @default "https" */
  protocol?: string;
  endpoints: {
    latencyData: string;
  };
  /** Configured for both `dev` and `prod` — see `getChartUrl`. Optional because `EnvironmentConfig` describes both, and a new environment added later might not have it filled in yet. */
  chartEndpoint?: ChartEndpointConfig;
}

interface AppConfig {
  dev: EnvironmentConfig;
  prod: EnvironmentConfig;
  venues?: Record<string, VenueConfig>;
}

const config = rawConfig as AppConfig;

/** CI sets `VITE_DEPLOY_ENV` from `DEPLOY_ENV`, which is `"prod"` only for
 * RT-tagged releases (see `.gitlab-ci.yml`) — everything else, including
 * local `vite dev`/`preview` where it's unset, is treated as dev. */
function resolveEnvironment(): Environment {
  return import.meta.env.VITE_DEPLOY_ENV === "prod" ? "prod" : "dev";
}

class ConfigHelper {
  private readonly environment: Environment;
  private readonly envConfig: EnvironmentConfig;
  private readonly venues: Record<string, VenueConfig>;

  constructor() {
    this.environment = resolveEnvironment();
    this.envConfig = config[this.environment];
    this.venues = config.venues ?? {};
  }

  getEnvironment(): Environment {
    return this.environment;
  }

  getApiBaseUrl(): string {
    // host has no trailing slash, path has no leading slash — join with exactly one.
    return `${this.envConfig.host}/${this.envConfig.path}`;
  }

  getEndpointUrl(name: keyof EnvironmentConfig["endpoints"]): string {
    return `${this.getApiBaseUrl()}${this.envConfig.endpoints[name]}`;
  }

  getVenue(venueKey: string): VenueConfig | undefined {
    return this.venues[venueKey];
  }

  /** The venue's instrument-catalog/depths URL (`venues[venueKey].endpoint.url`), if configured. */
  getVenueDepthsUrl(venueKey: string): string | undefined {
    return this.venues[venueKey]?.endpoint?.url;
  }

  /** Extra headers for the venue's depths endpoint, if any are configured. */
  getVenueDepthsHeaders(venueKey: string): Record<string, string> {
    return this.venues[venueKey]?.endpoint?.headers ?? {};
  }

  /** Extra headers (e.g. `x-lmax-chartkey`) required by the chart server, if configured for this environment. */
  getChartHeaders(): Record<string, string> {
    return this.envConfig.chartEndpoint?.headers ?? {};
  }
  
  getChartUrl(
    venueKey: string,
    symbol: string,
    fieldspec: string,
    interval: string,
    noStart: 0 | 1 = 1,
  ): string | undefined {
    const chartEndpoint = this.envConfig.chartEndpoint;
    const venue = this.venues[venueKey];
    if (!chartEndpoint || !venue) return undefined;

    const protocol = this.envConfig.protocol ?? "https";
    return chartEndpoint.chartPath
      .replace("{protocol}", protocol)
      .replace("{chartServer}", chartEndpoint.chartServer)
      .replace("{venue}", venue.venue)
      .replace("{symbol}", symbol)
      .replace("{fieldspec}", fieldspec)
      .replace("{interval}", interval)
      .replace("{noStart}", String(noStart));
  }
}

// Singleton — config doesn't change at runtime, no need to re-read per call.
export const configHelper = new ConfigHelper();
